import { useEffect, useMemo, useRef, useState } from "react";
import { SuperAdminSkip } from "@/components/site/SuperAdminSkip";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { PhoneField, TextField, TextareaField } from "@/components/site/fields";
import { isValidPhone } from "@/lib/phone";
import { reportFormError } from "@/lib/form-feedback";
import { saveContactDraft, submitContactMessage } from "@/lib/contact.functions";
import { contactSchemaForLocale } from "@/lib/contact-schema";
import { visitorFields } from "@/lib/analytics-client";
import { useFormPersist } from "@/lib/use-form-persist";
import { isNetworkError, networkErrorMessage, withRetry } from "@/lib/submit-retry";

const SESSION_KEY = "sis-contact-session";

/** Stabil sessionsnyckel per webbläsare så vi kan följa påbörjade meddelanden. */
function contactSessionId() {
  if (typeof window === "undefined") return "";
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const TEXT = {
  sv: {
    heading: "Skriv till oss",
    intro: "Vi svarar normalt inom en arbetsdag. Fält märkta med * behöver fyllas i.",
    name: "Namn",
    email: "E-post",
    phone: "Telefon",
    phoneHint: "Frivilligt – men gör det lättare för oss att ringa upp.",
    subject: "Ämne",
    subjectHint: "Frivilligt, t.ex. ”Plats i förskolan hösten 2027”.",
    message: "Meddelande",
    send: "Skicka meddelande",
    sending: "Skickar…",
    done: "Tack! Ditt meddelande är skickat.",
    doneTitle: "Tack – vi har fått ditt meddelande",
    doneBody:
      "Vi läser allt som kommer in och svarar på den e-postadress du angav. Har du fler frågor är det bara att skriva igen.",
    doneNext: "Vi svarar normalt inom en arbetsdag, helgfria vardagar.",
    doneSummary: "Så här läste vi ditt meddelande",
    doneAgain: "Skicka ett till meddelande",
    doneContact: "Brådskande? Ring +34 607 148 331 (telefontid 15:00–16:00) eller mejla ",

    error: "Något gick fel. Försök igen eller mejla oss direkt.",
    errName: "Fyll i ditt namn.",
    errEmail: "Fyll i din e-postadress.",
    errEmailFormat: "Kontrollera e-postadressen, t.ex. namn@exempel.se.",
    errPhone: "Fyll i ett giltigt telefonnummer med landskod.",
    errMessage: "Skriv några rader om vad du undrar.",
    errShort: "Skriv gärna lite mer så kan vi svara ordentligt.",
    summary: (n: number) =>
      n === 1 ? "1 fält behöver kompletteras." : `${n} fält behöver kompletteras.`,
  },
  en: {
    heading: "Write to us",
    intro: "We normally reply within one working day. Fields marked * are required.",
    name: "Name",
    email: "Email",
    phone: "Phone",
    phoneHint: "Optional – but it makes it easier for us to call you back.",
    subject: "Subject",
    subjectHint: "Optional, e.g. “Preschool place autumn 2027”.",
    message: "Message",
    send: "Send message",
    sending: "Sending…",
    done: "Thank you! Your message has been sent.",
    doneTitle: "Thank you – we've received your message",
    doneBody:
      "We read everything that comes in and will reply to the email address you gave us. Feel free to write again if anything else comes up.",
    doneNext: "We normally reply within one working day.",
    doneSummary: "This is what you sent us",
    doneAgain: "Send another message",
    doneContact: "Urgent? Call +34 607 148 331 (phone hours 3–4 pm) or email ",

    error: "Something went wrong. Please try again or email us directly.",
    errName: "Please enter your name.",
    errEmail: "Please enter your email address.",
    errEmailFormat: "Please check the email address, e.g. name@example.com.",
    errPhone: "Enter a valid phone number including country code.",
    errMessage: "Please write a few lines about your question.",
    errShort: "Please write a little more so we can answer properly.",
    summary: (n: number) => (n === 1 ? "1 field needs attention." : `${n} fields need attention.`),
  },
  es: {
    heading: "Escríbenos",
    intro: "Normalmente respondemos en un día laborable. Los campos marcados con * son obligatorios.",
    name: "Nombre",
    email: "Correo electrónico",
    phone: "Teléfono",
    phoneHint: "Opcional, pero nos facilita llamarte.",
    subject: "Asunto",
    subjectHint: "Opcional, por ejemplo «Plaza en preescolar otoño 2027».",
    message: "Mensaje",
    send: "Enviar mensaje",
    sending: "Enviando…",
    done: "¡Gracias! Hemos recibido tu mensaje.",
    doneTitle: "Gracias: hemos recibido tu mensaje",
    doneBody:
      "Leemos todo lo que nos llega y te responderemos al correo que nos has dado. Si surge algo más, escríbenos de nuevo.",
    doneNext: "Normalmente respondemos en un día laborable.",
    doneSummary: "Esto es lo que nos has enviado",
    doneAgain: "Enviar otro mensaje",
    doneContact: "¿Es urgente? Llama al +34 607 148 331 (de 15:00 a 16:00) o escribe a ",

    error: "Algo ha salido mal. Inténtalo de nuevo o escríbenos directamente.",
    errName: "Escribe tu nombre.",
    errEmail: "Escribe tu correo electrónico.",
    errEmailFormat: "Revisa el correo electrónico, por ejemplo nombre@ejemplo.com.",
    errPhone: "Escribe un teléfono válido con prefijo de país.",
    errMessage: "Cuéntanos brevemente tu consulta.",
    errShort: "Escribe un poco más para que podamos responderte bien.",
    summary: (n: number) => (n === 1 ? "1 campo necesita atención." : `${n} campos necesitan atención.`),
  },
} as const;

type FieldKey = "name" | "email" | "phone" | "message";

export function ContactForm({ locale = "sv" }: { locale?: "sv" | "en" | "es" }) {
  const t = TEXT[locale];
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const submit = useServerFn(submitContactMessage);
  const saveDraft = useServerFn(saveContactDraft);
  const sessionId = useRef("");
  const openedAt = useRef(Date.now());
  const [sent, setSent] = useState(false);
  const [touched, setTouched] = useState<Record<FieldKey, boolean>>({
    name: false,
    email: false,
    phone: false,
    message: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    company: "",
  });

  const clearDraft = useFormPersist(
    "contact",
    { ...form, company: "" },
    (saved) => setForm((prev) => ({ ...prev, ...saved, company: "" })),
  );

  useEffect(() => {
    sessionId.current = contactSessionId();
  }, []);

  /** Autospara det besökaren skrivit, max en gång per 1,5 sekund. */
  useEffect(() => {
    const filled = form.name || form.email || form.phone || form.subject || form.message;
    if (sent || !filled) return;
    const timer = window.setTimeout(() => {
      const id = sessionId.current || contactSessionId();
      if (!id) return;
      void saveDraft({
        data: {
          sessionId: id,
          name: form.name,
          email: form.email,
          phone: form.phone,
          subject: form.subject,
          message: form.message,
          locale,
          sourcePath: pathname,
        },
      }).catch(() => undefined);
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [form, sent, locale, pathname, saveDraft]);

  const errors = useMemo(() => {
    const e: Partial<Record<FieldKey, string>> = {};
    if (!form.name.trim()) e.name = t.errName;
    if (!form.email.trim()) e.email = t.errEmail;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = t.errEmailFormat;
    if (form.phone.trim() && !isValidPhone(form.phone)) e.phone = t.errPhone;
    if (!form.message.trim()) e.message = t.errMessage;
    else if (form.message.trim().length < 10) e.message = t.errShort;
    return e;
  }, [form.name, form.email, form.phone, form.message, t]);

  const shownError = (key: FieldKey) =>
    touched[key] || submitted ? errors[key] : undefined;

  const mutation = useMutation({
    mutationFn: () => {
      const payload = contactSchemaForLocale(locale).parse({
        ...form,
        locale,
        sourcePath: pathname,
        elapsedMs: Date.now() - openedAt.current,
        sessionId: sessionId.current,
        visitorId: visitorFields().visitorId,
      });
      return withRetry(() => submit({ data: payload }));
    },
    onSuccess: () => {
      setSent(true);
      clearDraft();
      toast.success(t.done, { id: "contact-form" });
    },
    onError: (e: Error) =>
      reportFormError(isNetworkError(e) ? networkErrorMessage(locale) : e.message || t.error, {
        toastId: "contact-form",
      }),
  });

  const doneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (sent) doneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [sent]);

  if (sent) {
    const rows = [
      { label: t.name, value: form.name },
      { label: t.email, value: form.email },
      { label: t.phone, value: form.phone },
      { label: t.subject, value: form.subject },
    ].filter((r) => r.value.trim());
    return (
      <div ref={doneRef} className="scroll-mt-28 rounded-xl border border-primary/30 bg-primary/5 p-8">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-primary">
          <CheckCircle2 className="size-5 shrink-0" aria-hidden="true" />
          {t.doneTitle}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.doneBody}</p>
        {rows.length ? (
          <div className="mt-5 rounded-lg border border-border bg-card p-4">
            <p className="text-sm font-medium">{t.doneSummary}</p>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              {rows.map((r) => (
                <div key={r.label}>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">{r.label}</dt>
                  <dd className="mt-0.5">{r.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t.doneNext}</p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t.doneContact}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">
            info@sweintschool.com
          </a>
          .
        </p>
        <div className="mt-6">
          <button
            type="button"
            className="inline-flex min-h-11 items-center rounded-md border border-border bg-card px-4 text-sm font-medium transition-colors hover:bg-background"
            onClick={() => {
              setSent(false);
              setSubmitted(false);
              setTouched({ name: false, email: false, phone: false, message: false });
              openedAt.current = Date.now();
              setForm({ name: "", email: "", phone: "", subject: "", message: "", company: "" });
            }}
          >
            {t.doneAgain}
          </button>
        </div>
      </div>
    );
  }


  return (
    <form
      noValidate
      className="rounded-xl border border-border bg-card p-6 shadow-soft"
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        const order: FieldKey[] = ["name", "email", "phone", "message"];
        const first = order.find((k) => errors[k]);
        if (first) {
          const count = order.filter((k) => errors[k]).length;
          reportFormError(`${t.summary(count)} ${errors[first]}`, {
            anchorId: `contact-${first}`,
            toastId: "contact-form",
          });
          return;
        }
        mutation.mutate();
      }}
    >
      <h2 className="font-display text-xl font-semibold">{t.heading}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t.intro}</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <TextField
          label={t.name}
          required
          anchorId="contact-name"
          autoComplete="name"
          value={form.name}
          error={shownError("name")}
          onBlur={() => setTouched((s) => ({ ...s, name: true }))}
          onChange={(v) => setForm({ ...form, name: v })}
        />
        <TextField
          label={t.email}
          required
          type="email"
          inputMode="email"
          anchorId="contact-email"
          autoComplete="email"
          value={form.email}
          error={shownError("email")}
          onBlur={() => setTouched((s) => ({ ...s, email: true }))}
          onChange={(v) => setForm({ ...form, email: v })}
        />
        <PhoneField
          label={t.phone}
          hint={t.phoneHint}
          value={form.phone}
          error={shownError("phone")}
          onBlur={() => setTouched((s) => ({ ...s, phone: true }))}
          onChange={(v) => setForm({ ...form, phone: v })}
        />
        <TextField
          label={t.subject}
          hint={t.subjectHint}
          value={form.subject}
          onChange={(v) => setForm({ ...form, subject: v })}
        />
      </div>

      <div className="mt-4">
        <TextareaField
          label={t.message}
          required
          rows={6}
          anchorId="contact-message"
          value={form.message}
          error={shownError("message")}
          onBlur={() => setTouched((s) => ({ ...s, message: true }))}
          onChange={(v) => setForm({ ...form, message: v })}
        />
      </div>

      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        value={form.company}
        onChange={(e) => setForm({ ...form, company: e.target.value })}
        className="hidden"
      />

      <button
        type="submit"
        disabled={mutation.isPending}
        className="mt-5 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {mutation.isPending ? t.sending : t.send}
      </button>
      <div><SuperAdminSkip onSkip={() => setSent(true)} /></div>
    </form>
  );
}
